import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, switchMap, throwError } from 'rxjs';
import Keycloak from 'keycloak-js';

import { AuthSessionService } from '@auth/services/auth-session.service';

/**
 * HTTP Interceptor that adds the Keycloak access token to all HTTP requests
 * and proactively refreshes the token when it is about to expire.
 */
export const tokenInterceptor: HttpInterceptorFn = (req, next) => {
  const keycloak = inject(Keycloak);
  const authSession = inject(AuthSessionService);

  // Skip token for Keycloak URLs, OIDC token endpoint, config, and public endpoints
  if (
    req.url.includes('keycloak') ||
    req.url.includes('openid-connect/token') ||
    req.url.includes('/auth/') ||
    req.url.includes('config.json')
  ) {
    return next(req);
  }

  // Get the current token
  const token = keycloak.token;

  // If token exists, add it to the request
  if (token) {
    // Check if token needs refresh (within 30 seconds of expiry)
    const tokenParsed = keycloak.tokenParsed;
    let needsRefresh = false;

    if (tokenParsed?.exp) {
      const expiresIn = tokenParsed.exp * 1000 - Date.now();
      // If token expires in less than 30 seconds, refresh it
      needsRefresh = expiresIn < 30000;
    }

    if (needsRefresh) {
      // Refresh token before making the request
      return from(keycloak.updateToken(30)).pipe(
        switchMap(() => {
          const updatedToken = keycloak.token;
          if (updatedToken) {
            const clonedReq = req.clone({
              setHeaders: {
                Authorization: `Bearer ${updatedToken}`,
              },
            });
            return next(clonedReq);
          }
          return next(req);
        }),
        catchError((error) => {
          authSession.logoutUnauthorized();
          return throwError(() => error);
        })
      );
    }

    // Add token to request
    const clonedReq = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });

    return next(clonedReq);
  }

  // No token available, proceed without it
  return next(req);
};
